import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { preOverquotaBanner, preQuotaOfferLimit } from 'reactApp/appHelpers/featuresHelpers/features/overquotaPre';
import { forceVigboLinkedAccount, vigboTariffBannerRemindDays, vigboTariffs } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { getLatestExpiredSubscription } from 'reactApp/modules/billingData/billingData.helpers';
import { BillingDataSelectors, getBillingDataLoadingState } from 'reactApp/modules/billingData/billingData.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { isAlertForOverquota } from 'reactApp/modules/features/features.helpers';
import { getUserOverquotaCycleCount } from 'reactApp/modules/profile/profile.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { getVigboAlbums, getVigboLinkState, hasActiveVigboTariff } from 'reactApp/modules/vigbo/vigbo.selectors';
import { VigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.types';
import type { RootState } from 'reactApp/store';
import { OverquotaBanner } from 'reactApp/ui/OverquotaBanner/OverquotaBanner';
import { PreOverquotaBanner } from 'reactApp/ui/PreOverquotaBanner/PreOverquotaBanner';
import { VigboEndTariffBanner } from 'reactApp/ui/VigboEndTariffBanner/VigboEndTariffBanner';
import { VigboRenewRemindBanner } from 'reactApp/ui/VigboRenewRemindBanner/VigboRenewRemindBanner';

export const useBanner = ({ isAllDocuments }) => {
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const isOverquota = useSelector(UserQuotaSelectors.isOverquota);
    const cycleCount = useSelector(getUserOverquotaCycleCount);
    const { used: usedPercent } = useSelector(UserQuotaSelectors.getFilledPercents);
    const storage = useSelector(getCurrentStorage);
    const showOverquotaBanner = Boolean(isOverquota && isAlertForOverquota && !isBizUser && !IS_ONPREMISE && !isAllDocuments);

    const isMobile = useSelector(EnvironmentSelectors.isMobile);
    const isVigboActiveTariff = useSelector(hasActiveVigboTariff);
    const { isLoading, isLoaded } = useSelector(getBillingDataLoadingState);
    const vigboAlbums = useSelector(getVigboAlbums);
    const vigboLinkState = useSelector(getVigboLinkState);
    const showVigboEndTariffBanner =
        (vigboLinkState === VigboAccountLinkState.linked || forceVigboLinkedAccount) &&
        storage === EStorageType.vigbo &&
        !isMobile &&
        !isLoading &&
        isLoaded &&
        !isVigboActiveTariff &&
        vigboAlbums.length > 0;

    const vigboSubs = useSelector((state: RootState) => BillingDataSelectors.getSubscriptionsByProductIds(state, vigboTariffs));
    const latestExpireTariff = useMemo(() => {
        const subsWithoutRenew = vigboSubs.filter((sub) => !sub.autorenewal);
        return getLatestExpiredSubscription(subsWithoutRenew);
    }, [vigboSubs]);
    const diffInMs = latestExpireTariff && latestExpireTariff.expires - Date.now();
    const diffInDays = diffInMs && Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const showVigboTariffEndRemindBanner =
        latestExpireTariff &&
        storage === EStorageType.vigbo &&
        !isMobile &&
        !isLoading &&
        isLoaded &&
        diffInDays &&
        diffInDays <= vigboTariffBannerRemindDays;

    const showPreOverquotaBanner =
        !showOverquotaBanner &&
        preOverquotaBanner &&
        !isPaidUser &&
        usedPercent >= preQuotaOfferLimit &&
        cycleCount >= 1 &&
        !isBizUser &&
        !IS_ONPREMISE &&
        !isAllDocuments;

    if (showVigboEndTariffBanner) {
        return <VigboEndTariffBanner />;
    }

    if (showOverquotaBanner) {
        return <OverquotaBanner />;
    }

    if (showVigboTariffEndRemindBanner) {
        return <VigboRenewRemindBanner tariff={latestExpireTariff} />;
    }

    if (showPreOverquotaBanner) {
        return <PreOverquotaBanner />;
    }

    return null;
};
